"use client";

import { useState } from "react";
import { AspectRatio } from "components/ui/aspect-ratio";
import Image from "next/image";

interface PlaylistItem {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  videoId: string;
}

interface YouTubePlaylistClientProps {
  playlistId: string;
  initialPlaylist: PlaylistItem[];
  initialPlaylistInfo: {
    title: string;
    description: string;
    thumbnail: string;
    channelTitle: string;
  };
  width?: number | string;
  height?: number | string;
  showPlaylist?: boolean;
  maxHeight?: string;
  dark?: boolean;
  className?: string;
}

export default function YouTubePlaylistClient({
  playlistId,
  initialPlaylist,
  showPlaylist = true,
  maxHeight = "280px",
  dark = false,
  className = "",
}: YouTubePlaylistClientProps) {
  const [currentVideo, setCurrentVideo] = useState<string>(
    initialPlaylist[0]?.videoId || "",
  );

  const embedUrl = currentVideo
    ? `https://www.youtube.com/embed/${currentVideo}?list=${playlistId}`
    : `https://www.youtube.com/embed/videoseries?list=${playlistId}`;

  return (
    <div className={`${className}`}>
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="flex-1">
          <AspectRatio ratio={16 / 9}>
            <iframe
              src={embedUrl}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="YouTube Playlist"
              className="rounded-lg"
            />
          </AspectRatio>
        </div>

        {showPlaylist && initialPlaylist.length > 0 && (
          <div
            className={`w-full ${dark ? "text-white" : "text-black"} max-xl:hidden lg:w-80`}
          >
            <div
              className={`max-h-[${maxHeight}] overflow-y-auto rounded-lg ${dark ? "scrollbar-dark bg-black" : "bg-gray-50"} p-4 `}
            >
              <h3 className="mb-4 font-bold">
                Playlist ({initialPlaylist.length} video
                {initialPlaylist.length !== 1 ? "s" : ""})
              </h3>
              <div className="space-y-2">
                {initialPlaylist.map((item) => (
                  <div
                    key={item.id}
                    className={`flex cursor-pointer gap-3 rounded p-2 transition-colors ${dark ? "hover:bg-gray-900" : "hover:bg-gray-200"} ${
                      currentVideo === item.videoId
                        ? `border-l-4 ${dark ? "border-blue-800 bg-gray-800" : "border-blue-500 bg-blue-100"} `
                        : ""
                    }`}
                    onClick={() => setCurrentVideo(item.videoId)}
                  >
                    <div className="w-28 ">
                      <AspectRatio ratio={16 / 9}>
                        <Image
                          fill
                          unoptimized
                          src={item.thumbnail}
                          alt={item.title}
                          className="h-full w-full flex-shrink-0 rounded object-cover"
                        />
                      </AspectRatio>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="mb-1 line-clamp-2 text-sm font-medium">
                        {item.title}
                      </h4>
                      {item.description && (
                        <p className="line-clamp-2 text-xs text-gray-500">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
